import { useTranslation } from 'react-i18next';
import { Tag, Percent } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { formatPrice } from '@/lib/utils';

interface ProductPricingDisplayProps {
  price: number;
  currency: string;
  prices?: {
    usd?: number | string;
    euro?: number | string;
  } | null;
  comparePrice?: number;
  discount?: number;
}

export function ProductPricingDisplay({
  price,
  currency,
  prices,
  comparePrice,
  discount,
}: ProductPricingDisplayProps) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tag className="size-5" />
          {t('products.pricing')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span>{t('products.price')}</span>
          <div className="text-right">
            <span className="text-2xl font-bold">{formatPrice(price, currency)}</span>
            {prices && (
              <div className="text-xs text-muted-foreground mt-1">
                {prices.usd && <span className="mr-2">{formatPrice(prices.usd, 'USD')}</span>}
                {prices.euro && <span>{formatPrice(prices.euro, 'EUR')}</span>}
              </div>
            )}
          </div>
        </div>

        {comparePrice && comparePrice > price && (
          <div className="flex items-center justify-between">
            <span>{t('products.comparePrice')}</span>
            <span className="text-lg text-muted-foreground line-through">
              {formatPrice(comparePrice, currency)}
            </span>
          </div>
        )}

        {discount && discount > 0 && (
          <div className="flex items-center justify-between">
            <span>{t('products.discount')}</span>
            <Badge variant="destructive" className="gap-1">
              <Percent className="size-3" />
              -{discount}%
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
